import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PlusIcon, FunnelIcon } from "@heroicons/react/24/outline";
import { useNavigate, useLocation } from "react-router-dom";
import { UploadDocumentModal } from "../components/UploadDocumentModal";
import { DocumentReviewCard } from "../components/DocumentReviewCard";
import { DocumentReviewsTable } from "../components/DocumentReviewsTable";
import { useDocumentReviews } from "../hooks/useDocumentReview";
import { useAuth } from "../../../contexts/AuthContext";
import type { AnalysisStatus } from "../types/document-review.types";
import { Select, Button } from "@/shared/ui";
import { usePlanLimits } from "../../../hooks/usePlanLimits";
import { getCountries, type CountryConfig } from "@/shared/config";

export const DocumentReviewPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuth();
  const { subscription } = usePlanLimits();

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");
  const [filters, setFilters] = useState<{
    country?: string;
    documentType?: string;
    status?: AnalysisStatus;
  }>({});
  const [countries, setCountries] = useState<CountryConfig[]>([]);

  useEffect(() => {
    getCountries().then(setCountries);
  }, []);

  // Determine base path from current location
  const basePath = location.pathname.includes("/lawyer")
    ? "/dashboard/lawyer"
    : location.pathname.includes("/client")
      ? "/dashboard/client"
      : "";

  const { reviews, loading, error, refetch } = useDocumentReviews(filters);

  const handleUploadSuccess = (reviewId: string) => {
    refetch();
    navigate(`${basePath}/document-review/${reviewId}`);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      if (key === "status") {
        newFilters.status = (value as AnalysisStatus) || undefined;
      } else if (key === "country") {
        newFilters.country = value || undefined;
      } else if (key === "documentType") {
        newFilters.documentType = value || undefined;
      }
      return newFilters;
    });
  };

  const clearFilters = () => {
    setFilters({});
  };

  const hasActiveFilters =
    filters.country || filters.documentType || filters.status;

  return (
    <div className="mx-auto py-4">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <h1 className="text-3xl font-bold text-juridico">
              {t("documentReview.title")}
            </h1>
            <p className="text-sm font-light text-tiber-700 mt-2">
              {t("documentReview.description")}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-sm">
              {profile?.user_type !== "lawyer" && (
                <span className="text-tiber-700">{t("common.credits")}:</span>
              )}
              <span className="ml-1 font-semibold text-tiber-600">
                {profile?.user_type === "lawyer"
                  ? subscription?.planType
                      ?.replace(/^lawyer_/, "")
                      .replace(/^\w/, (char: string) => char.toUpperCase()) ||
                    "Professional"
                  : profile?.credits_remaining || 0}
              </span>
              <span className="ml-1.5 text-tiber-500/80 font-medium tracking-tight">
                {profile?.user_type === "lawyer"
                  ? "Plan"
                  : t("documentReview.statCards.creditsValue")}
              </span>
            </div>
            <Button
              onClick={() => setIsUploadModalOpen(true)}
              disabled={
                !profile ||
                (profile.user_type !== "lawyer" &&
                  profile.credits_remaining < 1)
              }
            >
              <PlusIcon className="h-5 w-5" />
              {t("documentReview.uploadDocument")}
            </Button>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="bg-white rounded-lg border border-tiber-200 p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-5 w-5 text-titan" />
              <span className="text-sm font-medium text-tiber-700">
                {t("common.filters")}:
              </span>
            </div>
            {/* Country Filter */}
            <Select
              value={filters.country || ""}
              onChange={(e) => handleFilterChange("country", e.target.value)}
              options={[
                { value: "", label: t("common.allCountries") },
                ...countries.map((c) => ({
                  value: c.code,
                  label:
                    i18n.language === "es" ? c.nameEs : c.nameEn,
                })),
              ]}
            />

            {/* Status Filter */}
            <Select
              value={filters.status || ""}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              options={[
                { value: "", label: t("common.allStatuses") },
                {
                  value: "completed",
                  label: t("documentReview.status.completed"),
                },
                { value: "pending", label: t("documentReview.status.pending") },
                {
                  value: "processing",
                  label: t("documentReview.status.processing"),
                },
                { value: "failed", label: t("documentReview.status.failed") },
              ]}
            />

            {/* View Mode Toggle */}
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => setViewMode("grid")}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  viewMode === "grid"
                    ? "bg-tiber-100 text-tiber-700"
                    : "text-tiber-700 hover:bg-tiber-100"
                }`}
              >
                {t("common.grid")}
              </button>
              <button
                onClick={() => setViewMode("table")}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  viewMode === "table"
                    ? "bg-tiber-100 text-tiber-700"
                    : "text-tiber-700 hover:bg-tiber-100"
                }`}
              >
                {t("common.table")}
              </button>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-tiber-600 hover:text-tiber-700 font-medium"
              >
                {t("common.clearFilters")}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 border-4 border-tiber-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-12">
          <div className="max-w-md mx-auto">
            <div className="h-24 w-24 mx-auto mb-4 rounded-full bg-tiber-100 flex items-center justify-center">
              <FunnelIcon className="h-12 w-12 text-titan" />
            </div>
            <h3 className="text-lg font-medium text-juridico mb-2">
              {hasActiveFilters
                ? t("documentReview.noResultsFiltered")
                : t("documentReview.noReviews")}
            </h3>
            <p className="text-tiber-700 mb-6">
              {hasActiveFilters
                ? t("documentReview.tryDifferentFilters")
                : t("documentReview.startByUploading")}
            </p>
            {!hasActiveFilters && (
              <Button
                onClick={() => setIsUploadModalOpen(true)}
                disabled={
                  !profile ||
                  (profile.user_type !== "lawyer" &&
                    profile.credits_remaining < 1)
                }
              >
                <PlusIcon className="h-5 w-5" />
                {t("documentReview.uploadDocument")}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* Grid View */}
          {viewMode === "grid" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.map((review) => (
                <DocumentReviewCard
                  key={review.id}
                  review={review}
                  basePath={basePath}
                />
              ))}
            </div>
          )}

          {/* Table View */}
          {viewMode === "table" && (
            <div className="bg-white rounded-lg border border-tiber-200">
              <DocumentReviewsTable reviews={reviews} basePath={basePath} />
            </div>
          )}
        </>
      )}

      {/* Upload Modal */}
      <UploadDocumentModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onSuccess={handleUploadSuccess}
      />
    </div>
  );
};
